<template lang="pug">
el-collapse-item.item-consulta-processada(:name='item.idRequest')
    template(slot='title')
        .d-flex.align-items-center.pr-2
            el-button(type='danger' size='mini' round icon='el-icon-delete' @click.stop="$emit('remover', item.idRequest)")
            span.item-consulta-processada__valor.ml-2 {{ rotuloExibicao }}
    p(v-if='item.erro') {{ $t('mensagens.diagnosticoSocioambiental.erro.ValorNaoEncontrado') }}
    section(v-else-if='item.dados')
        .d-flex.justify-content-between.align-items-center.mb-2
            h4.mb-0
                i.el-icon-finished.mr-2
                | {{ $t('mensagens.diagnosticoSocioambiental.resultadoDaAnalise') }}
            .d-flex
                el-tooltip(:content="$t('mensagens.diagnosticoSocioambiental.zoomGeometria')" placement='top')
                    el-button(size='mini' type='primary' icon='mdi mdi-crosshairs-gps' @click="$emit('zoom', item)")
                el-tooltip(v-if='podeAnalisarProximidade' :content="$t('mensagens.diagnosticoSocioambiental.analisarProximidades')" placement='top')
                    el-button.ml-1(size='mini' type='primary' icon='el-icon-connection' :loading='loadingProximidade' @click='analisarProximidade')
                el-tooltip(:content="$t('mensagens.diagnosticoSocioambiental.marcarParaExportacao')" placement='top')
                    el-button.ml-1(size='mini' type='primary' :icon="marcadoParaExportacao ? 'el-icon-circle-check' : 'el-icon-document-add'" @click="$emit('selecionar-exportacao', item)")
                el-tooltip(:content="$t('mensagens.diagnosticoSocioambiental.exportarDiagnostico')" placement='top')
                    el-button.ml-1(size='mini' type='primary' icon='el-icon-download' :loading='item.loadingDownloadPdf' @click="$emit('exportar', item)")
        InformacoesGeraisV2(
            :informacoes-gerais='item.informacoesGerais'
            :imovel='item.dados && item.dados.imovel'
            :produtividade='item.produtividade'
            :protocolo-utilizado-formatado='item.protocoloUtilizadoFormatado'
            :tipo='item.tipo'
            :pesquisado='item.documento'
            :exibir-capacidade='item.exibirCapacidadeProdutiva'
        )
        DiagnosticoSocioambiental.mt-3(
            v-if='aberto'
            :dadosConsulta='item.dados'
            :mapaLeaflet='mapaLeaflet'
            :habilitar-opacidade='true'
            :habilitar-zoom-restricao='true'
            :habilitar-sinal-aviso='true'
            :fechar-collapses='true'
            :centralizar-mapa-ao-abrir-restricao='false'
            :index-collapse='index'
            v-bind:cpfCnpj='cpfCnpjParaCamada'
            v-bind:car='carParaCamada'
            @index-aberto='repassarIndexAberto'
        )
        section.item-consulta-processada__proximidade.mt-3(v-if='diagnosticoProximidade')
            h4
                i.el-icon-connection.mr-2
                | {{ diagnosticoProximidade.tituloProximidade }}
            DiagnosticoSocioambiental(
                :dadosConsulta='diagnosticoProximidade'
                :mapaLeaflet='mapaLeaflet'
                :habilitar-sinal-aviso='diagnosticoProximidade.habilitarAlertaProximidade'
                :habilitar-zoom-restricao='true'
                :centralizar-mapa-ao-abrir-restricao='false'
                :index-collapse='index + 1000'
                v-bind:cpfCnpj='cpfCnpjParaCamada'
                v-bind:car='carParaCamada'
                @index-aberto='repassarIndexAberto'
            )
</template>

<script>
import DiagnosticoSocioambiental from '@/components/diagnosticoSocioambiental/index.vue';
import DiagnosticoSocioambientalService from '@/services/DiagnosticoSocioambientalService';
import formatters from '@/utils/formatters';
import InformacoesGeraisV2 from './InformacoesGeraisV2.vue';

export default {
    name: 'ItemConsultaProcessada',
    components: { DiagnosticoSocioambiental, InformacoesGeraisV2 },
    props: {
        item: { type: Object, required: true },
        index: { type: Number, required: true },
        aberto: { type: Boolean, default: false },
        // vem da lista do store (marcarExportarPDF), não de uma flag no item - ver DiagnosticoSocioambientalV2.vue
        marcadoParaExportacao: { type: Boolean, default: false },
        mapaLeaflet: { type: Object, default: null },
        codigoProtocolo: { type: String, default: null }
    },
    data() {
        return { loadingProximidade: false };
    },
    computed: {
        // RN07: o item é identificado pelo código informado na consulta (CAR, documento, parcela) ou pelo nome do
        // arquivo vetorial. Parcela escolhida no modal do INCRA aparece pelo código da parcela, como no PS e na v1.
        // Documento sai com máscara de CPF/CNPJ só aqui, na exibição: consulta, duplicidade e PDF usam o número limpo.
        rotuloExibicao() {
            return this.item.tipo === 'CPF_CNPJ'
                ? formatters.formatarDocumentoInput(String(this.item.documento))
                : this.item.documento;
        },
        // Mirror: DiagnosticoSocioambiental.vue:1008-1019 (v1) - o diagnóstico de proximidades
        // fica registrado no próprio item (levantado pelo pai via @proximidade-processada) em
        // vez de estado local, pra mostrarAreasDiagnosticoNoMapa do pai conseguir lê-lo.
        diagnosticoProximidade() {
            return this.item.diagnosticoProximidade || null;
        },
        podeAnalisarProximidade() {
            return Boolean(this.item.idDiagnosticoSocioambiental) && Boolean(this.geometria);
        },
        geometria() {
            return this.item.dados?.imovel?.geometria || this.item.dados?.geometria || null;
        },
        // Mirror: DiagnosticoSocioambiental.vue:477-495 (v1, getCpfCnpj/getCar) - props de
        // identificação passadas pro componente compartilhado (index.vue), usadas pros links de
        // "consultar certidão" (IBAMA/LDI). Ver index.vue:145,152.
        cpfCnpjParaCamada() {
            return this.item.tipo === 'CPF_CNPJ' ? this.item.documento : null;
        },
        carParaCamada() {
            const codigoImovel = this.item.dados?.imovel?.codigo;
            const documento = codigoImovel ||
                (['CODIGOCAR', 'CAR_ESTADUAL_MT'].includes(this.item.tipo) ? this.item.documento : null);
            if (!documento) return null;
            const normalizado = String(documento).toUpperCase();
            return (normalizado.startsWith('PA') || normalizado.startsWith('MT')) ? documento : null;
        }
    },
    methods: {
        // Fix: handler inline (`@index-aberto="$emit('camada-aberta', $event)"`) só repassava o
        // 1º dos 3 argumentos emitidos por index.vue:556-561. Referência de método preserva os 3
        // (mesmo padrão da v1, DiagnosticoSocioambiental.vue:136,154).
        repassarIndexAberto(indexAberto, indexCollapse, indexCollapseRow) {
            this.$emit('camada-aberta', indexAberto, indexCollapse, indexCollapseRow);
        },
        async analisarProximidade() {
            this.loadingProximidade = true;
            try {
                const geometrias = (Array.isArray(this.geometria) ? this.geometria : [this.geometria])
                    .map(geometria => JSON.stringify(geometria));
                const { data } = await DiagnosticoSocioambientalService.analisarBuffer(
                    geometrias,
                    this.item.idDiagnosticoSocioambiental,
                    this.codigoProtocolo
                );
                this.$emit('proximidade-processada', this.item.idRequest, {
                    diagnosticos: (data.dados || []).reduce((todos, grupo) => todos.concat(grupo), []),
                    geometria: (data.geometrias || []).map(geometria => JSON.parse(geometria)),
                    habilitarAlertaProximidade: data.habilitarAlertaProximidade,
                    tituloProximidade: data.tituloProximidade
                });
            } catch (erro) {
                this.$notify.error({ message: this.$t('mensagens.diagnosticoSocioambiental.erro.buscarDadosBuffer') });
            } finally {
                this.loadingProximidade = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
// Mirror do card da lista do PS (DiagnosticosSocioambientais.vue, CSS :2609-2617/:2713-2715):
// borda + raio + padding + espaço entre cards, cabeçalho sem borda de collapse.
.item-consulta-processada {
    border: 1px solid #dddede;
    border-radius: 5px;
    padding: 10px;
    margin-bottom: 10px;

    &__valor {
        font-weight: 500;
        color: var(--primary-font-color);
        font-size: 14px;
    }

    // Faixa cinza da proximidade, mirror PS-A :2694-2710 (fundo #d7dada42).
    &__proximidade {
        background-color: #d7dada42;
        padding: 5px 10px;
        border-radius: 5px;

        h4 {
            display: flex;
            align-items: center;
        }
    }

    h4 {
        font-size: 14px;
        font-weight: 500;
        color: var(--primary-font-color);
    }

    ::v-deep .el-collapse-item__wrap {
        border-bottom: 0;
    }

    // Accordion de camadas (componente compartilhado index.vue, raiz #diagnostico-socioambiental).
    // Fix visual do bug `classe = +' ml-2'` (index.vue:montaClasseCamada, vira NaN e nunca aplica
    // negrito): aplicado só dentro deste card via ::v-deep, sem tocar no componente compartilhado
    // (index.vue tem 16 outras telas consumidoras).
    ::v-deep #diagnostico-socioambiental {
        .checkbox-container > span {
            font-weight: 500;
            color: var(--primary-font-color);
        }
    }
}
</style>
